/**
 * SmoothMenu — popover with a slider to relax / smooth the current edit
 * selection toward neighbour averages (uniform Laplacian).
 *
 * Opens from the mesh context menu or Shift+O. Captures the active
 * object's vertex buffer once on open, then re-runs the smooth op from
 * that baseline on every slider tick — matches the execute-and-refine
 * pattern established by RelaxMenu (pose) and GravityMenu (object /
 * edit).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useStore } from "../app/store.ts";
import { FloatingCommandMenu } from "./FloatingCommandMenu.tsx";
import { useDeferredUndo } from "./hooks/useDeferredUndo.ts";
import { useOperatorMenu } from "./hooks/useOperatorMenu.ts";
import { Slider } from "./Slider.tsx";
import { SegmentedControl } from "./shared.tsx";
import {
  COLOR_TEXT_DIM,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_SMALL,
} from "./tokens.ts";

const ITERATION_OPTIONS = [
  { label: "1", value: 1 },
  { label: "2", value: 2 },
  { label: "4", value: 4 },
  { label: "8", value: 8 },
] as const;

// All three axes always enabled — matches Blender's default. A per-axis
// mask is supported at the WASM op level and can be surfaced later if
// users need it.
const AXIS_MASK_ALL = 0b111;

export function SmoothMenu() {
  const menu = useOperatorMenu("smooth");
  const undo = useDeferredUndo();

  const [factor, setFactor] = useState(0);
  const [iterations, setIterations] = useState(1);

  // Baseline vertex buffer captured when the menu opens. The smooth op
  // runs from these on every tick so dragging the slider produces a
  // consistent result instead of compounding.
  const baselineRef = useRef<Float32Array | null>(null);

  const reapply = useCallback(
    (nextFactor: number, nextIterations: number) => {
      const baseline = baselineRef.current;
      if (!baseline) return;
      undo.pushOnce();
      useStore
        .getState()
        .smoothSelectionFrom(
          baseline,
          nextFactor / 100,
          nextIterations,
          AXIS_MASK_ALL,
        );
    },
    [undo],
  );

  const onFactorChange = useCallback(
    (v: number) => {
      setFactor(v);
      reapply(v, iterations);
    },
    [iterations, reapply],
  );

  const onIterationsChange = useCallback(
    (v: number) => {
      setIterations(v);
      reapply(factor, v);
    },
    [factor, reapply],
  );

  const close = useCallback(() => {
    setFactor(0);
    setIterations(1);
    baselineRef.current = null;
    undo.reset();
    menu.close();
  }, [menu, undo]);

  // On open: snapshot the active object's vertices. A fresh copy is
  // required because patchSceneObjectAfterEdit will replace the store's
  // vertex buffer with each slider tick — if we captured by reference,
  // the "baseline" would drift into the already-smoothed result and
  // the effect would compound instead of re-running from rest.
  //
  // Dependencies intentionally omit `undo` (whose identity changes
  // every render) so this only fires on the open/close transition,
  // not on every slider-driven re-render. Matches GravityMenu's
  // baseline-capture pattern.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run on open change
  useEffect(() => {
    if (!menu.open) return;
    setFactor(0);
    setIterations(1);
    undo.reset();

    const s = useStore.getState();
    const em = s.editMode;
    if (!em) {
      baselineRef.current = null;
      return;
    }
    const obj = s.sceneObjects.get(em.objectId);
    if (!obj) {
      baselineRef.current = null;
      return;
    }
    baselineRef.current = new Float32Array(obj.vertices);
  }, [menu.open]);

  return (
    <FloatingCommandMenu
      open={menu.open}
      onClose={close}
      title="Smooth Vertices"
      width={240}
      anchorPoint={menu.anchorPoint}
    >
      <div style={rowStyle}>
        <span style={labelStyle}>Factor</span>
        <Slider
          value={factor}
          min={0}
          max={100}
          onChange={onFactorChange}
          dimChromeOnDrag
        />
        <span style={valueDisplayStyle}>{factor}%</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Repeat</span>
        <SegmentedControl
          options={
            ITERATION_OPTIONS as unknown as { label: string; value: number }[]
          }
          value={iterations}
          onChange={onIterationsChange}
        />
      </div>
    </FloatingCommandMenu>
  );
}

const rowStyle = {
  padding: "4px 6px",
  display: "flex",
  alignItems: "center",
  gap: 8,
} as const;

const labelStyle = {
  fontSize: FONT_SIZE_SMALL,
  color: COLOR_TEXT_DIM,
  width: 40,
  flexShrink: 0,
} as const;

const valueDisplayStyle = {
  fontSize: FONT_SIZE_SMALL,
  color: COLOR_TEXT_SECONDARY,
  width: 36,
  textAlign: "right" as const,
  fontVariantNumeric: "tabular-nums" as const,
  flexShrink: 0,
};
